import {
  Timestamp,
  collection,
  doc,
  getDocs,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { db } from '@/lib/firebaseConfig';
import {
  FILE_MERGE_BUFFER_KEY,
  FILES_SUBCOLLECTION,
  USERS_COLLECTION,
} from '../constants';

interface SerializedFile {
  fileId: string;
  fileName: string;
  storagePath: string;
  size: number;
  mimeType: string;
  createdAtMs: number | null;
  expiresAtMs: number | null;
  hidden?: boolean;
  purgeAtMs?: number | null;
  passwordProtected?: boolean;
}

interface MergeBuffer {
  anonUid: string;
  files: SerializedFile[];
}

const filesCol = (uid: string) =>
  collection(db, USERS_COLLECTION, uid, FILES_SUBCOLLECTION);

export async function bufferAnonFilesForMerge(anonUid: string): Promise<void> {
  try {
    const snap = await getDocs(filesCol(anonUid));
    const now = Date.now();

    const files: SerializedFile[] = snap.docs
      .map((d) => {
        const data = d.data();
        const isHidden = data.hidden === true;
        if (data.deletedAt && !isHidden) return null;
        const expiresAtMs: number | null = data.expiresAt?.toMillis?.() ?? null;
        if (expiresAtMs !== null && expiresAtMs <= now) return null;
        return {
          fileId: d.id,
          fileName: data.fileName as string,
          storagePath: data.storagePath as string,
          size: data.size as number,
          mimeType: (data.mimeType as string) || '',
          createdAtMs: data.createdAt?.toMillis?.() ?? null,
          expiresAtMs,
          ...(typeof data.passwordProtected === 'boolean'
            ? { passwordProtected: data.passwordProtected }
            : {}),
          ...(isHidden
            ? {
                hidden: true,
                purgeAtMs: data.purgeAt?.toMillis?.() ?? null,
              }
            : {}),
        };
      })
      .filter((x): x is SerializedFile => x !== null);

    if (files.length > 0) {
      const buffer: MergeBuffer = { anonUid, files };
      localStorage.setItem(FILE_MERGE_BUFFER_KEY, JSON.stringify(buffer));
    }
  } catch (err) {
    console.error('bufferAnonFilesForMerge failed:', err);
  }
}

export async function applyPendingFileMerge(realUid: string): Promise<void> {
  try {
    const raw = localStorage.getItem(FILE_MERGE_BUFFER_KEY);
    if (!raw) return;

    const buffer: MergeBuffer = JSON.parse(raw);
    if (!buffer.files?.length) {
      localStorage.removeItem(FILE_MERGE_BUFFER_KEY);
      return;
    }

    await Promise.all(
      buffer.files.map((entry) => {
        const newStoragePath = entry.storagePath.replace(
          `users/${buffer.anonUid}/`,
          `users/${realUid}/`
        );
        return setDoc(
          doc(filesCol(realUid), entry.fileId),
          {
            fileName: entry.fileName,
            storagePath: newStoragePath,
            size: entry.size,
            mimeType: entry.mimeType,
            createdAt: entry.createdAtMs
              ? Timestamp.fromMillis(entry.createdAtMs)
              : null,
            expiresAt: entry.expiresAtMs
              ? Timestamp.fromMillis(entry.expiresAtMs)
              : null,
            deletedAt: entry.hidden ? serverTimestamp() : null,
            purgeAt:
              entry.hidden && entry.purgeAtMs
                ? Timestamp.fromMillis(entry.purgeAtMs)
                : null,
            ...(entry.hidden ? { hidden: true } : {}),
            ...(typeof entry.passwordProtected === 'boolean'
              ? { passwordProtected: entry.passwordProtected }
              : {}),
          },
          { merge: true }
        );
      })
    );

    localStorage.removeItem(FILE_MERGE_BUFFER_KEY);
  } catch (err) {
    console.error('applyPendingFileMerge failed:', err);
  }
}
